import { useCallback, useRef, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';

interface UseUnsavedChangesGuardOptions {
  when: boolean;
  onSaveAndLeave: () => Promise<boolean>;
}

export function useUnsavedChangesGuard({ when, onSaveAndLeave }: UseUnsavedChangesGuardOptions) {
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!when) return;
        event.preventDefault();
      },
      [when],
    ),
    { capture: true },
  );

  const [isBlocked, setIsBlocked] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Intercept an in-form navigation action (e.g. a Cancel button) and show the
  // unsaved-changes dialog if there are unsaved changes; otherwise navigate immediately.
  const requestNavigation = useCallback(
    (action: () => void) => {
      if (!when) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setIsBlocked(true);
    },
    [when],
  );

  const handleLeave = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setIsBlocked(false);
    action?.();
  }, []);

  const handleStay = useCallback(() => {
    pendingActionRef.current = null;
    setIsBlocked(false);
  }, []);

  const handleSaveAndLeave = useCallback(async () => {
    const saved = await onSaveAndLeave();
    if (saved) {
      handleLeave();
    }
  }, [handleLeave, onSaveAndLeave]);

  return {
    isBlocked,
    requestNavigation,
    handleLeave,
    handleStay,
    handleSaveAndLeave,
  };
}
